import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Overline } from '@/components/atoms/Eyebrow';
import { Disclosure } from '@/components/molecules/Disclosure';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    ConfirmRegistration,
    defaultOperationDate,
    PenPicker,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { type TimelineFishGroup, useSiteFishGroupActions } from '@/domain/fishGroups';

import { earliestEventDay, eventTimestamp } from '../../helpers/siteActions/actionDate';
import { currentStay, stayBioscopeIds } from '../../helpers/siteActions/groupFacts';
import { delousingConfirmItems, moveConfirmItems } from '../../helpers/siteActions/moveConfirm';
import { movePenOptions } from '../../helpers/siteActions/penOptions';
import type { SitePen } from '../../helpers/siteActions/sitePens';
import ModalFormActions from '../ModalFormActions/ModalFormActions';
import ActionDialogTitle from '../SiteActionDialog/ActionDialogTitle';
import EventDateField from '../SiteActionDialog/EventDateField';
import PensStatus, { pensKnown, type SitePensState } from '../SiteActionDialog/PensStatus';
import TreatmentFields from '../SiteActionDialog/TreatmentFields';

import styles from '../SiteActionDialog/SiteActionDialog.module.scss';

interface DelousingFormValues {
    date: Date | null;
    treatmentMethod: string;
    notes: string;
    targetCageId: number | null;
}

interface SiteDelousingModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    group: TimelineFishGroup;
    pens: readonly SitePen[];
    pensState: SitePensState;
    onClose: () => void;
    onDone: () => void;
}

/**
 * A whole-group delousing (Figma 663:89325). "Add moving of fish group" turns it into a move
 * with the delousing on the same date, one request, confirmed first like every move.
 */
export default function SiteDelousingModal(props: SiteDelousingModalProps) {
    const { clientId, locationId, group, pens, pensState, onClose, onDone } = props;
    const { t } = useTranslation('fishGroups');
    const [withMove, setWithMove] = useState(false);
    const [confirming, setConfirming] = useState<DelousingFormValues | null>(null);
    const actions = useSiteFishGroupActions(clientId, locationId);
    const serverError = useServerErrorMessage(actions.state.error);

    const stay = currentStay(group);
    const bioscopeIds = stayBioscopeIds(stay);
    const earliest = earliestEventDay(stay?.from ?? null);
    const penOptions = movePenOptions(pens, stay?.cageId ?? null, t);
    const penLabel = (cageId: number | null) =>
        pens.find((pen) => pen.cageId === cageId)?.displayName ?? '';

    const { handleSubmit, control, formState } = useForm<DelousingFormValues>({
        defaultValues: {
            date: defaultOperationDate(),
            treatmentMethod: '',
            notes: '',
            targetCageId: null,
        },
    });
    const submitting = formState.isSubmitting || actions.state.isLoading;

    const close = () => {
        if (submitting) return;
        actions.state.reset();
        onClose();
    };

    const finish = (key: string) => {
        ToastInfo({
            content: t(key, { name: group.name }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'site-fish-group-delousing-success', autoClose: 5000 },
        });
        actions.state.reset();
        onDone();
    };

    const register = async (values: DelousingFormValues) => {
        if (!values.date) return;
        const delousing = { treatmentMethod: values.treatmentMethod, notes: values.notes };
        if (withMove && values.targetCageId !== null) {
            const moved = await actions
                .move({
                    fishGroupId: group.id,
                    targetCageId: values.targetCageId,
                    date: eventTimestamp(values.date, stay?.from ?? null),
                    bioscopeMoves: [],
                    delousing,
                })
                .then(
                    () => true,
                    () => false
                );
            if (moved) finish('siteActions.delousing.movedToast');
            return;
        }
        const recorded = await actions
            .recordDelousing({
                fishGroupId: group.id,
                date: eventTimestamp(values.date, stay?.from ?? null),
                ...delousing,
            })
            .then(
                () => true,
                () => false
            );
        if (recorded) finish('siteActions.delousing.successToast');
    };

    // No undo for a customer: a delousing is confirmed like every other registration.
    const onSubmit = (values: DelousingFormValues) => setConfirming(values);

    const title = (
        <ActionDialogTitle
            kind="delousing"
            title={t('siteActions.delousing.title')}
            groupName={group.name}
            penName={stay?.cageName ?? null}
            bioscopeIds={bioscopeIds}
        />
    );

    if (confirming?.date) {
        const fromPen = stay?.cageName ?? '';
        const moving = withMove && confirming.targetCageId !== null;
        const toPen = penLabel(confirming.targetCageId);
        return (
            <ModalComponent
                isOpen
                size="lg"
                onClose={close}
                aria-label={t('siteActions.confirm.title')}
                backdrop="static"
                body={
                    <ConfirmRegistration
                        fromPens={moving ? [fromPen] : []}
                        toPens={moving ? [toPen] : []}
                        title={t('siteActions.confirm.title')}
                        items={
                            moving
                                ? moveConfirmItems(
                                      {
                                          date: confirming.date,
                                          fromPen,
                                          toPen,
                                          targetBioscopeIds:
                                              pens.find(
                                                  (pen) => pen.cageId === confirming.targetCageId
                                              )?.bioscopeIds ?? [],
                                          bioscopeIds,
                                          bioscopesMove: false,
                                          withDelousing: true,
                                      },
                                      t
                                  )
                                : delousingConfirmItems(
                                      {
                                          date: confirming.date,
                                          groupName: group.name,
                                          penName: fromPen,
                                          treatmentMethod: confirming.treatmentMethod,
                                      },
                                      t
                                  )
                        }
                        error={serverError && <p className={styles.serverError}>{serverError}</p>}
                        cancelLabel={t('actions.cancel')}
                        confirmLabel={t('siteActions.confirm.confirm')}
                        isSubmitting={submitting}
                        onCancel={() => {
                            actions.state.reset();
                            setConfirming(null);
                        }}
                        onConfirm={() => register(confirming)}
                    />
                }
            />
        );
    }

    const body = (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <EventDateField control={control} name="date" earliest={earliest} />
            <TreatmentFields control={control} methodName="treatmentMethod" notesName="notes" />
            <Disclosure
                variant="inline"
                title={t('siteActions.delousing.addMove')}
                open={withMove}
                onOpenChange={setWithMove}>
                <section className={styles.section}>
                    <Overline as="h3" className={styles.sectionLabel}>
                        {t('siteActions.form.destinationPen')}
                    </Overline>
                    <PensStatus state={pensState} />
                    <Controller
                        control={control}
                        name="targetCageId"
                        rules={{ validate: (value) => !withMove || value !== null }}
                        render={({ field, fieldState }) => (
                            <>
                                <PenPicker
                                    options={penOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    aria-label={t('siteActions.form.destinationPen')}
                                />
                                {fieldState.error && (
                                    <p className={styles.serverError} role="alert">
                                        {t('siteActions.form.pickPen')}
                                    </p>
                                )}
                            </>
                        )}
                    />
                </section>
            </Disclosure>
            {serverError && <p className={styles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t('siteActions.delousing.submit')}
                isSubmitting={submitting}
                submitDisabled={withMove && !pensKnown(pensState)}
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={close}
            title={title}
            body={body}
            backdrop="static"
        />
    );
}
